import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { LOAN_PRODUCTS } from "@/lib/loanProducts";
import { ArrowRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";

const fmt = (v) => `$${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const productTitle = (key) => LOAN_PRODUCTS.find((p) => p.key === key)?.title || key;

export default function MyLoans() {
  const [loans, setLoans] = useState([]);
  useEffect(() => {
    api.get("/loans/me").then(({ data }) => setLoans(data || []));
  }, []);

  return (
    <DashboardLayout role="client">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-semibold text-ink sm:text-3xl">My loans</h1>
        <p className="mt-1 text-sm text-ink-soft">Active and historical loans, with up-to-the-minute balances.</p>
      </div>

      {loans.length === 0 && (
        <div className="rounded-xl border border-[#E5E2DE] bg-white px-5 py-10 text-center text-sm text-ink-soft">
          You don't have any loans yet. Once an application is approved, it'll appear here.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {loans.map((l) => {
          const pct = Math.min(100, (l.amount_paid / l.total_repayable) * 100);
          return (
            <div key={l.id} data-testid={`loan-card-${l.id}`} className="rounded-xl border border-[#E5E2DE] bg-white p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-heading text-lg font-semibold text-ink">{productTitle(l.product)}</div>
                  <div className="text-xs text-ink-muted">Disbursed {new Date(l.disbursed_on).toLocaleDateString()}</div>
                </div>
                <span className={`rounded-full border px-2 py-0.5 text-xs ${l.status === "active" ? "border-emerald-300 bg-emerald-100 text-emerald-900" : "border-violet-300 bg-violet-100 text-violet-900"}`}>
                  {l.status}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wider text-ink-muted">Principal</div>
                  <div className="mt-0.5 font-semibold text-ink">{fmt(l.principal)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-ink-muted">Rate</div>
                  <div className="mt-0.5 font-semibold text-ink">{l.interest_rate_pct}%</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-ink-muted">Term</div>
                  <div className="mt-0.5 font-semibold text-ink">{l.term_months} mo</div>
                </div>
              </div>

              <div className="mt-5">
                <div className="flex items-center justify-between text-xs text-ink-soft">
                  <span>{fmt(l.amount_paid)} paid</span>
                  <span>{fmt(l.balance)} remaining</span>
                </div>
                <Progress value={pct} className="mt-1.5 h-2" />
              </div>

              <Link to={`/dashboard/loans/${l.id}`} className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#5B2C82] hover:text-[#E87722]" data-testid={`loan-details-link-${l.id}`}>
                View schedule <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
